import { ApolloProvider, useApolloClient } from '@apollo/react-hooks'
import type { OfflineQuotesQuery, AppDocsQuery } from '@paintscout/api'
import {
  AppDocsDocument,
  OfflineQuotesDocument,
  updateCachedContact,
  updateCachedQuote,
  useAppDocsQuery
} from '@paintscout/api'
import { getHighestRole, isMasqueradeCompanyNotFoundError } from '@paintscout/util/users'
import * as Sentry from '@sentry/react'
import { createLogger } from '@paintscout/logger'
import { SnackbarProvider, TranslationProvider } from '@paintscout/ui'
import { DevPermissionsProvider, DevUserProvider, Theme, UserProvider } from '@paintscout/ui-business'
import type ApolloCache from '@wora/apollo-cache'
import type ApolloClient from 'apollo-client'
import { error as analyticsError } from 'shared/util/analytics'
import type { Auth0Session } from 'react-auth0'
import type { QuoteDocument } from 'paintscout'
import React, { startTransition, useEffect, useRef, useState } from 'react'
import { Authorizer, useAuth } from 'react-auth0'
import { useConnection } from 'react-detect-offline'
import { useHistory } from 'react-router-dom'
import { ApolloQueue, createApolloClient } from 'shared/graphql'
import AppLoading from 'src/components/App/AppLoading'
import { getAuthTokenResult } from 'shared/graphql/client'
import {
  getCacheScopeKey,
  getMasqueradeCompanyId,
  MASQUERADE_CHANGED_EVENT,
  resolveMasqueradeCompanyId
} from 'shared/graphql/masquerade'
import LaunchDarkly from 'shared/components/common/LaunchDarkly'
import { isEmbeddedInIframe } from 'src/util/iframe'
import ClientOptionsProvider from './ClientOptionsProvider'
import EmbeddedAppNotice from './EmbeddedAppNotice'
import MasqueradeUnavailable from './MasqueradeUnavailable'
import { buildUserForProvider } from './buildUserForProvider'
import {
  clearAuthRecoveryMark,
  recentlyAttemptedAuthRecovery,
  recoverFromMissingToken,
  UNEXPECTED_ERROR_MESSAGE
} from 'shared/util/auth/authRecovery'

const log = createLogger('authorize-user')

/**
 * Rejects if `promise` doesn't settle within `ms`. Used to guard the IndexedDB calls during
 * startup: WebKit's IndexedDB is known to hang on `open()` (never firing success/error) after an
 * iOS update until the origin's website data is cleared. Without a timeout the app would sit on
 * the loading spinner forever; with it we fall back to online-only mode and still render.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_resolve, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms))
  ])
}

export default function AuthorizeUser({ children }: { children?: React.ReactNode }) {
  const { session, auth0Client } = useAuth()

  const history = useHistory()

  const isPainterAccount = session?.app_metadata?.roles?.includes('painter')
  const isSuperadmin = session?.app_metadata?.roles?.includes('superadmin')
  const isSupport = session?.app_metadata?.roles?.includes('support')
  const isCorporate = session?.app_metadata?.roles?.includes('corporate')
  const [client, setClient] = useState<ApolloClient<any>>(null)
  const canMasquerade = !!(isSuperadmin || isSupport || isCorporate)
  const locationSearch = history.location.search

  const [masqueradeCompanyId, setMasqueradeCompanyId] = useState(() =>
    resolveMasqueradeCompanyId({ canMasquerade, search: locationSearch })
  )

  // Scopes every company-scoped cache to (user, masqueraded company). Bare user_id when not
  // masquerading, so normal users keep their existing offline store.
  const cacheScope = getCacheScopeKey(session?.user_id, masqueradeCompanyId)

  // `search` matters as much as `pathname` here: navigating /dashboard -> /dashboard?masqueradeCompanyId=X
  // leaves the pathname untouched, so keying only on pathname would ignore the company switch entirely.
  //
  // `canMasquerade` matters too: on first render `session` is still null, so this re-resolves once the
  // session arrives and the URL param becomes readable.
  useEffect(() => {
    setMasqueradeCompanyId(resolveMasqueradeCompanyId({ canMasquerade, search: locationSearch }))
  }, [history.location.pathname, locationSearch, canMasquerade])

  // Same-tab signal from setMasqueradeCompanyId/clearMasqueradeCompanyId, so entering or leaving an
  // account applies immediately rather than depending on the caller's navigation changing the route.
  // Reads storage directly instead of re-resolving: an explicit in-app switch is the freshest intent,
  // and a stale `?masqueradeCompanyId=` still sitting in the URL would otherwise win over it.
  //
  // Note this is NOT the cross-tab `storage` event that used to live here. Masquerade context is
  // per-tab now (see masquerade.ts), so one tab entering an account can no longer re-point, purge, or
  // navigate any other tab.
  useEffect(() => {
    function handleMasqueradeChange() {
      setMasqueradeCompanyId(canMasquerade ? getMasqueradeCompanyId() : null)
    }

    window.addEventListener(MASQUERADE_CHANGED_EVENT, handleMasqueradeChange)

    return () => {
      window.removeEventListener(MASQUERADE_CHANGED_EVENT, handleMasqueradeChange)
    }
  }, [canMasquerade])

  useEffect(() => {
    /**
     * Set by this effect's cleanup. Everything below runs after several awaits — the IDB support
     * check (up to 4s), hydration (up to 5s) and the token request (up to 15s) — and each of the
     * three can settle after the component has gone.
     *
     * The token branch is the one that mattered (ENG-1190). Click Log Out a second or two after a
     * page load and the route switches to /logout, which unmounts this component; the token request
     * that page already fired is aborted by the navigation, Safari rejects it with
     * `TypeError: Load failed`, and this effect then ran `recoverFromMissingToken` from a dead
     * render — stashing `/logout` as the post-login return URL and making the user log in twice.
     */
    let cancelled = false

    async function supportsIdb() {
      await new Promise((resolve, reject) => {
        const db = window.indexedDB.open('test')

        db.onerror = reject

        db.onsuccess = () => {
          window.indexedDB.deleteDatabase('test')
          resolve(null)
        }
      })
    }

    async function init() {
      if (session) {
        // Masquerade sessions are deliberately online-only. Persisting a customer's data on a staff
        // member's device bought nothing — support/superadmin/corporate users are never offline in a
        // customer account — and it carried the entire cross-company corruption surface: a per-company
        // IDB store to hydrate from, a persisted mutation queue replayed later against whatever
        // company was current, and the purge/re-seed dance that leaked one company's options into
        // another's session (ENG-1061). With persistence off there is no store to hydrate, no queue
        // to replay, and nothing to purge.
        let enableOffline = !isPainterAccount && !masqueradeCompanyId
        if (enableOffline) {
          try {
            // check if idb is supported on this browser — time-boxed because WebKit can hang here
            await withTimeout(supportsIdb(), 4000, 'IndexedDB support check')
          } catch (e) {
            enableOffline = false
            console.log(e)
            // Not alerted after unmount: the check is time-boxed at 4s, and a modal about offline
            // support is noise once the user has navigated away (most often straight to /logout).
            if (!cancelled) {
              window.alert(
                'Your browser is unsupported or running in incognito/private mode. If you go offline, you will be unable to save your changes.'
              )
            }
          }
        }

        try {
          const client = createApolloClient({
            // scoped per masqueraded company — a shared store let one company's $options/$rates
            // hydrate into another company's session (ENG-1061). Belt-and-braces now that
            // masquerading disables persistence outright.
            idbName: cacheScope,
            enableOffline,
            // bound at creation, never re-read from storage per request: this client can only ever
            // speak to this company, so an in-flight request can't be re-pointed mid-switch
            masqueradeCompanyId
          })

          try {
            // time-boxed: a hung IDB never throws, so a plain await here could block render forever
            await withTimeout(client.hydrate(), 5000, 'IDB hydration')
          } catch (hydrateError) {
            // IDB data may be stale, corrupted, or hanging — continue without offline support
            console.warn('IDB hydration failed, falling back to online-only mode:', hydrateError)
            enableOffline = false
          }
          await new Promise((resolve) => setTimeout(resolve))
          if (cancelled) return
          setClient(client)

          // `getAuthTokenResult` resolves rather than throwing (so a stalled token request can't
          // wedge every GraphQL operation), so read the outcome off the result instead of
          // catching.
          //
          // The trigger is `!token`, not `isInvalidGrant(error)`. Gating on `invalid_grant` alone
          // meant a timeout or a `missing_refresh_token` fell straight through to the render below
          // with no token — every GraphQL request then went out unauthenticated, the API answered
          // `jwt must be provided`, and `LoadData`'s error effect pushed the user to /logout with
          // "An unexpected error occurred". Sentry shows those two firing one second apart, same
          // user, same URL, every time (ENG-1190). Whatever the reason, no token means we cannot
          // load the app, and the only real recovery is the one the user was performing by hand:
          // log in again.
          const { token, error: tokenError, timedOut } = await getAuthTokenResult()

          if (token) {
            // Cleared even when cancelled, deliberately. The mark arms `recentlyAttemptedAuthRecovery`
            // for two minutes, and a token proves the last recovery worked. Skipping this on unmount
            // left it armed, so the next real failure inside the window escalated straight to /logout
            // with "An unexpected error occurred" instead of re-prompting for login.
            clearAuthRecoveryMark()
          } else if (!cancelled) {
            recoverFromMissingToken({
              auth0Client,
              push: (path, state) => history.push(path, state),
              error: tokenError,
              timedOut
            })
          }
        } catch (error) {
          // `console.error`, not `console.log`: this branch now catches more than it used to (the
          // token path routes real failures through here), and a `log` is invisible in a captured
          // console.
          console.error('Unable to authorize user', error)
          Sentry.captureException(error, { tags: { message: 'Unable to authorize user' } })
          // Still reported when cancelled — a real failure is worth the Sentry event — but never
          // navigated: the user has already left this tree, and pushing /logout under them turns a
          // deliberate account switch into an error dialog.
          if (!cancelled) {
            history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
          }
        }
      }
    }

    init()

    return () => {
      cancelled = true
    }
    // re-init client if session.user_id changes
  }, [session?.user_id, isPainterAccount, masqueradeCompanyId]) // eslint-disable-line

  // react-auth0's Authorizer redirects unconditionally whenever there's no session (see its
  // useEffect) — inside a cross-origin iframe with no session (most commonly iOS Safari, which
  // partitions iframe storage away from any top-level session), that redirect lands on Auth0's
  // universal login, which refuses to render in a frame, leaving a blank, stuck iframe (#19007).
  // Bail out before Authorizer ever mounts. An iframe that already has a session (e.g. desktop,
  // which isn't as strict about storage partitioning) is unaffected — it never needs to redirect.
  if (!session && isEmbeddedInIframe()) {
    return <EmbeddedAppNotice />
  }

  return (
    <Authorizer>
      {!client ? (
        <AppLoading withRecovery />
      ) : (
        // key by (user_id, masqueraded company) so the whole tree — including the providers holding
        // options/rates — remounts on a company switch. Keying on user_id alone meant masquerading
        // never remounted, leaving the previous company's settings in memory (ENG-1061).
        <ApolloProvider key={cacheScope} client={client}>
          <ApolloQueue>
            <LoadData session={session} masqueradeCompanyId={masqueradeCompanyId}>
              {children}
            </LoadData>
          </ApolloQueue>
        </ApolloProvider>
      )}
    </Authorizer>
  )
}

// fetch necessary data and put in appropriate providers
const LoadData = ({
  children,
  session,
  masqueradeCompanyId
}: {
  children?: React.ReactNode
  session: Auth0Session
  masqueradeCompanyId?: string | null
}) => {
  const history = useHistory()
  const apolloClient = useApolloClient()
  const { authenticate } = useAuth()
  const { online } = useConnection()
  const isPainterAccount = session?.app_metadata?.roles?.includes('painter')
  const roles = session?.app_metadata?.roles ?? []
  const isPrivileged = roles.includes('superadmin') || roles.includes('support') || roles.includes('corporate')

  const [launchDarklyReady, setLaunchDarklyReady] = useState(false)
  // Last `lastPermissionsUpdated` timestamp we already forced a token refresh for — see the
  // permissions effect below.
  const attemptedPermissionsRefresh = useRef<string | undefined>()
  // Nothing is persisted while masquerading (see the `enableOffline` note in AuthorizeUser), so there
  // is no stale store to purge — and the purge itself was a liability here: it ran off the shared
  // `lastPurge` key and re-seeded appDocs through the cache reset, which is how one company's
  // options/rates used to survive into another company's session (ENG-1061).
  const [purging, setPurging] = useState(() => online && !isPainterAccount && !masqueradeCompanyId && checkNeedsPurge())

  function handleUnexpectedError() {
    history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
  }

  useEffect(() => {
    // `session` can go null on a render this component still commits (see the loading guard
    // below) — e.g. mid account-switch. Nothing to report to Sentry in that case; the next
    // render with a real session re-runs this and sets it correctly.
    if (!session) return

    Sentry.setUser({
      username: session.name,
      email: session.email,
      id: session.user_id
    })

    const role = getHighestRole(session.app_metadata.roles)
    Sentry.setTags({
      role,
      company: session.app_metadata.company?.name,
      companyId: session.app_metadata.companyId
    })
  }, [session])

  useEffect(() => {
    if (purging) {
      purge()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purging])

  async function purge() {
    try {
      const appDocs = apolloClient.readQuery({
        query: AppDocsDocument
      })
      const offlineQuotes = apolloClient.readQuery<OfflineQuotesQuery>({
        query: OfflineQuotesDocument
      })

      await apolloClient.cache.reset()

      window.localStorage.setItem('lastPurge', JSON.stringify(Date.now()))

      // Re-seeding appDocs keeps offline users working across a purge. Masquerade sessions never get
      // here (purging is disabled above), but keep the guard so the re-seed can't carry one company's
      // documents through a cache reset into another company's session if that ever changes (ENG-1061).
      if (!masqueradeCompanyId || !online) {
        apolloClient.writeQuery({
          query: AppDocsDocument,
          data: appDocs
        })
      }

      updateCache(offlineQuotes)
    } catch (error) {
      // if lastPurge is undefined, then this is expected because it's probably a brand new database
      if (JSON.parse(window.localStorage.getItem('lastPurge'))) {
        log.error('Unable to purge cache', { error: error?.error ?? error, sentry: true })
      }
    }
    setPurging(false)
  }

  /**
   * updates various cached queries using results of offlineQuotes
   */
  function updateCache(offlineQuotes: OfflineQuotesQuery) {
    if (offlineQuotes) {
      // put appDocs back into cache
      apolloClient.writeQuery({
        query: OfflineQuotesDocument,
        data: offlineQuotes
      })

      // get response of every offline quote and update the quote caches
      const { quotes = [], invoices = [], contacts = [], templates = [] } = offlineQuotes.offlineQuotes ?? {}

      const allQuotes = [...quotes, ...invoices, ...templates]
      allQuotes.forEach((quote) => {
        updateCachedQuote(apolloClient.cache as ApolloCache, quote)
      })

      contacts.forEach((contact) => {
        updateCachedContact(apolloClient.cache as ApolloCache, contact as any)
      })
    }
  }

  const appDocsQuery = useAppDocsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    onCompleted: async () => {
      const unsavedNewQuotes: QuoteDocument[] = []

      // we're online - try fetching offline quotes
      const offlineQuotesResponse = await apolloClient
        .query<OfflineQuotesQuery>({
          query: OfflineQuotesDocument,
          // network-only will still write to cache
          fetchPolicy: 'network-only'
        })
        .catch(() => null)

      if (offlineQuotesResponse) {
        updateCache(offlineQuotesResponse.data)

        const all = [
          ...(offlineQuotesResponse.data?.offlineQuotes?.quotes ?? []),
          ...(offlineQuotesResponse.data?.offlineQuotes?.invoices ?? [])
        ]

        // temporary check to make sure all new offline quotes made it
        // (this could be a false positive if 10 new offline quotes come in and push it beyond the limit)
        unsavedNewQuotes.forEach((quote) => {
          if (!all.find((q) => q._id === quote._id)) {
            console.error('Potentially lost offline quote', quote)
            analyticsError('Potentially lost offline quote', quote)
          }
        })
      }
    }
  })

  // The masqueraded company can't be loaded at all (deleted/renamed/bad id). Reported server-side
  // with full context, so don't re-report it here — just offer a way out below.
  const masqueradeUnavailable = isMasqueradeCompanyNotFoundError(appDocsQuery.error)

  useEffect(() => {
    if (appDocsQuery.error && !appDocsQuery.data?.appDocs && !isPrivileged && !masqueradeUnavailable) {
      // A token recovery is already in flight (see `recoverFromMissingToken`): this client mounted
      // before the token check resolved, so its appDocs request went out unauthenticated and came
      // back `jwt must be provided`. That is a symptom of the failure being recovered from, not a
      // separate fault — reporting it and pushing the "An unexpected error occurred" dialog would
      // race the login redirect and show the user the exact screen this change removes.
      if (recentlyAttemptedAuthRecovery()) return

      Sentry.captureException(appDocsQuery.error, { tags: { message: 'Unexpected error in AuthorizeUser' } })
      handleUnexpectedError()
    }
  }, [appDocsQuery.error])

  const appDocs = appDocsQuery?.data?.appDocs

  useEffect(() => {
    if (!appDocs) return
    const prefsTimestamp = (appDocs.preferences as any)?.lastPermissionsUpdated as string | undefined
    const jwtTimestamp = (session?.app_metadata as any)?.lastPermissionsUpdated as string | undefined
    // At most one forced refresh per distinct timestamp: `ignoreCache: true` is a rotating
    // refresh-token exchange, and a mismatch that never reconciles (e.g. the Auth0-side
    // app_metadata write failed) used to force one on every preferences `_rev` change and every
    // page load — grant churn that feeds the ENG-1190 dead-token failures. Skipped offline: the
    // exchange can't succeed and would burn the one attempt for this timestamp.
    if (
      prefsTimestamp &&
      prefsTimestamp !== jwtTimestamp &&
      online &&
      attemptedPermissionsRefresh.current !== prefsTimestamp
    ) {
      attemptedPermissionsRefresh.current = prefsTimestamp
      authenticate({ ignoreCache: true })
        .then(() => apolloClient.resetStore())
        .catch((err) => {
          console.error('Failed to refresh token after permissions update', err)
        })
    }
  }, [(appDocs?.preferences as any)?._rev])

  if (masqueradeUnavailable && !appDocs) {
    return <MasqueradeUnavailable companyId={masqueradeCompanyId} />
  }

  // `appDocsQuery` uses `cache-and-network`, so `appDocs` can be non-null from cache on a render
  // where `session` has already gone null (e.g. mid account-switch, before the effect above has
  // re-run to tear down/recreate the client). Treat that the same as still loading instead of
  // crashing on the unguarded `session.app_metadata` reads below (Sentry PAINTSCOUT-APP-CVG).
  if ((!appDocs && !isPainterAccount) || !session || purging) {
    return <AppLoading withRecovery />
  }

  const preferences = (appDocs?.preferences as any) ?? {}
  const companyDetails = appDocs?.companyDetails ?? session.app_metadata.company
  // use the prop rather than re-reading storage: this tree's Apollo client and cache are scoped to
  // exactly this company, and reading storage here could hand ClientOptionsProvider a different one
  // mid-switch (ENG-1061)
  const companyId = masqueradeCompanyId || session.app_metadata.companyId

  const _stage = process.env.DEPLOYMENT_STAGE ?? 'local'
  const showDevTools =
    _stage === 'local' ||
    _stage === 'dev' ||
    (_stage === 'test' && window.localStorage.getItem('ps:devtools') === 'true')

  const innerContent = (
    <>
      {/*
        lang is intentionally hardcoded to 'en' because the internal app still has no per-user language preference.
        If that changes, swap this to a real preference value and move the provider lower only if runtime language
        changes need to be scoped to smaller translated subtrees.
      */}
      <TranslationProvider lang="en">
        <LaunchDarkly
          onReady={() => startTransition(() => setLaunchDarklyReady(true))}
          // the resolved prop, not localStorage: this has to agree with the `companyId` below and with
          // the cacheScope key that remounts this tree, or LaunchDarkly evaluates one company's flags
          // against another's identity (ENG-1061)
          isMasquerading={!!masqueradeCompanyId}
          userDetails={{
            id: session.user_id,
            companyId,
            parentCompanyId: companyDetails?.parentCompanyId || null,
            email: session.email
          }}
        >
          <Theme>
            <SnackbarProvider>{launchDarklyReady || !online ? children : <AppLoading />}</SnackbarProvider>
          </Theme>
        </LaunchDarkly>
      </TranslationProvider>
    </>
  )

  const userProvider = (
    <UserProvider
      user={buildUserForProvider({
        session,
        companyDetails,
        // the prop, not storage: this tree's Apollo client is bound to this company, so reading
        // storage here could disagree with the data actually on screen. Older code paths also write
        // the literal string 'null', which is truthy — that alone was enough to force isOwner: true
        // for a user who isn't masquerading at all (ENG-1061)
        isMasquerading: !!masqueradeCompanyId
      })}
      isMasquerading={!!masqueradeCompanyId}
      preferences={preferences}
      remainingQuotes={appDocs?.remainingQuotes ?? 0}
      togglePaymentExpiryNoticeCache={() => {
        if (!appDocs) return
        apolloClient.cache.writeQuery<AppDocsQuery>({
          query: AppDocsDocument,
          data: {
            ...appDocsQuery.data,
            appDocs: {
              ...appDocs,
              companyDetails: { ...appDocs.companyDetails, paymentExpiryNotice: false }
            }
          }
        })
      }}
    >
      {showDevTools ? (
        <DevUserProvider onMockChange={() => apolloClient.resetStore().catch(() => {})}>{innerContent}</DevUserProvider>
      ) : (
        innerContent
      )}
    </UserProvider>
  )

  return (
    <ClientOptionsProvider clientId={companyId}>
      {showDevTools ? <DevPermissionsProvider>{userProvider}</DevPermissionsProvider> : userProvider}
    </ClientOptionsProvider>
  )
}

const checkNeedsPurge = () => {
  const lastPurge = JSON.parse(window.localStorage.getItem('lastPurge')) || 0
  const needsPurge = lastPurge < Date.now() - 1000 * 60 * 60 * 24 // once a day

  return needsPurge
}
